import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/**
 * GlassSheet — translucent LIGHT glass bottom/side sheet. Overlays the KALKI
 * environment without replacing it. Used by CHAT and intelligence satellites.
 */
export default function GlassSheet({ open, onClose, title, eyebrow, children, side = "bottom", testid }) {
  const base = "fixed z-[80] backdrop-blur-2xl bg-[rgba(255,255,255,0.92)] border border-black/5 shadow-[0_-30px_80px_-30px_rgba(30,20,70,0.18)] text-[#1C1C1C]";
  const positionCls =
    side === "bottom"
      ? "left-0 right-0 bottom-0 rounded-t-[32px] max-h-[82vh]"
      : side === "right"
      ? "top-0 right-0 h-full w-full sm:max-w-md rounded-l-[32px]"
      : "top-0 left-0 h-full w-full sm:max-w-md rounded-r-[32px]";

  const initial = side === "bottom" ? { y: "100%" } : side === "right" ? { x: "100%" } : { x: "-100%" };
  const animate = side === "bottom" ? { y: 0 } : { x: 0 };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[70] bg-[rgba(30,20,70,0.28)] backdrop-blur-md"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            data-testid={testid}
            className={`${base} ${positionCls}`}
            initial={initial} animate={animate} exit={initial}
            transition={{ type: "spring", damping: 28, stiffness: 240 }}
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-[3px] mt-3 rounded-full"
                 style={{ background: "linear-gradient(90deg, transparent, rgba(93,0,255,0.4), rgba(255,46,154,0.4), transparent)" }} />
            <div className="relative p-6 sm:p-8 overflow-y-auto max-h-[82vh] kalki-scroll">
              <button
                onClick={onClose}
                aria-label="Close"
                data-testid="sheet-close"
                type="button"
                className="absolute top-5 right-5 h-9 w-9 rounded-full border border-black/10 flex items-center justify-center text-[#676773] hover:text-[#1C1C1C] hover:border-[#5D00FF] transition-colors bg-white"
                style={{ zIndex: 10 }}
              >
                <X className="w-4 h-4" />
              </button>
              {eyebrow && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="h-[1px] w-6" style={{ background: "linear-gradient(90deg, transparent, #5D00FF, #FF2E9A, transparent)" }} />
                  <span className="text-[10px] font-semibold tracking-[0.3em] uppercase"
                        style={{ background: "linear-gradient(90deg,#5D00FF,#FF2E9A)", WebkitBackgroundClip: "text", color: "transparent" }}>
                    {eyebrow}
                  </span>
                </div>
              )}
              {title && <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-[#1C1C1C] mb-6">{title}</h2>}
              <div className="text-[#1C1C1C]">{children}</div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
